import axios, { AxiosInstance } from 'axios'
import {
    RedmineConfig, Issue, Project, User, DashboardStats, ActivityItem,
    TimeEntry, TimeEntryActivity, Document, DocumentCategory, News,
    Forum, ForumTopic, ForumMessage, WikiPage, Version, Repository,
    Changeset, Role, Group, CustomField, IssueStatus, Workflow,
    Enumeration, SearchResult, Tracker, Status, Priority
} from './types'

export class RedmineClient {
    private client: AxiosInstance

    constructor(config: RedmineConfig) {
        this.client = axios.create({
            baseURL: `${config.baseUrl}/`,
            headers: {
                'Content-Type': 'application/json',
                'X-Redmine-API-Key': config.apiKey || '',
            },
            auth: config.username && config.password ? {
                username: config.username,
                password: config.password,
            } : undefined,
        })
    }

    // Issues
    async getIssues(params?: any): Promise<{ issues: Issue[], total_count: number }> {
        const response = await this.client.get('/issues.json', { params })
        return response.data
    }

    async getIssue(id: number, params?: any): Promise<Issue> {
        // getIssue called
        const response = await this.client.get(`/issues/${id}.json`, { params })
        // getIssue response received
        return response.data.issue
    }

    async createIssue(issue: Partial<Issue>): Promise<Issue> {
        const response = await this.client.post('/issues.json', { issue })
        return response.data.issue
    }

    async updateIssue(id: number, issue: Partial<Issue>): Promise<Issue> {
        try {
            // Redmine API returns 204 No Content on successful PUT
            const putResponse = await this.client.put(`/issues/${id}.json`, { issue })

            // Fetch the updated issue to return it
            const response = await this.client.get(`/issues/${id}.json`)

            // Check if status actually changed
            const requestedStatusId = (issue as any).status_id

            if (requestedStatusId && response.data.issue.status.id !== requestedStatusId) {
            }

            return response.data.issue
        } catch (error: any) {
            console.error('❌ RedmineClient: Error updating issue:', error.response?.status, error.response?.data)
            // Enhanced error handling for Redmine workflow errors
            if (error.response) {
                const status = error.response.status
                const errorData = error.response.data

                // Handle workflow/validation errors (422 Unprocessable Entity)
                if (status === 422 && errorData.errors) {
                    const errorMessages = Array.isArray(errorData.errors)
                        ? errorData.errors
                        : [errorData.errors]
                    throw new Error(`Validation failed: ${errorMessages.join(', ')}`)
                }

                // Handle forbidden (403) - workflow not allowed
                if (status === 403) {
                    throw new Error('This status transition is not allowed by the workflow. Please check with your administrator.')
                }

                // Handle not found
                if (status === 404) {
                    throw new Error(`Issue #${id} not found`)
                }
            }

            // Re-throw original error if not handled above
            throw error
        }
    }

    async deleteIssue(id: number): Promise<void> {
        await this.client.delete(`/issues/${id}.json`)
    }

    // Projects
    async getProjects(params?: any): Promise<{ projects: Project[], total_count?: number }> {
        const response = await this.client.get('/projects.json', { params })
        return response.data
    }

    async getProject(id: number, params?: any): Promise<Project> {
        const response = await this.client.get(`/projects/${id}.json`, { params })
        return response.data.project
    }

    async createProject(project: Partial<Project>): Promise<Project> {
        const response = await this.client.post('/projects.json', { project })
        return response.data.project
    }

    async updateProject(id: number, project: Partial<Project>): Promise<Project> {
        const response = await this.client.put(`/projects/${id}.json`, { project })
        return response.data.project
    }

    async deleteProject(id: number): Promise<void> {
        await this.client.delete(`/projects/${id}.json`)
    }

    // Users
    async getUsers(params?: any): Promise<{ users: User[], total_count?: number }> {
        const response = await this.client.get('/users.json', { params })
        return response.data
    }

    async getCurrentUser(): Promise<User> {
        const response = await this.client.get('/my/account.json')
        return response.data.user
    }

    async getUser(id: number): Promise<User> {
        const response = await this.client.get(`/users/${id}.json`)
        return response.data.user
    }

    async createUser(user: Partial<User>): Promise<User> {
        const response = await this.client.post('/users.json', { user })
        return response.data.user
    }

    async updateUser(id: number, user: Partial<User>): Promise<User> {
        const response = await this.client.put(`/users/${id}.json`, { user })
        return response.data.user
    }

    async deleteUser(id: number): Promise<void> {
        await this.client.delete(`/users/${id}.json`)
    }

    // Dashboard & Analytics
    async getDashboardStats(userId?: number): Promise<DashboardStats> {
        // Optimize: Batch all API calls that can run in parallel
        const today = new Date().toISOString().split('T')[0]
        const weekStart = new Date()
        weekStart.setDate(weekStart.getDate() - weekStart.getDay())
        const weekStartStr = weekStart.toISOString().split('T')[0]

        // Single batch of all required API calls
        const [
            assignedIssuesOpen,
            assignedIssuesClosed,
            activity,
            timeEntries,
            projects
        ] = await Promise.all([
            // Get assigned open issues (limit to 100 for better accuracy)
            this.getIssues({
                assigned_to_id: userId || 'me',
                status_id: 'open',
                limit: 100
            }),
            // Get closed issues this week for completion rate
            this.getIssues({
                assigned_to_id: userId || 'me',
                status_id: 'closed',
                updated_on: `>=${weekStartStr}`,
                limit: 100
            }),
            // Get recent activity
            this.getActivities({ limit: 10 }),
            // Get today's time entries
            this.getTimeEntries({
                user_id: userId || 'me',
                spent_on: today
            }),
            // Get projects (limit to 50 for faster response)
            this.getProjects({ limit: 50 })
        ])

        // Process results locally instead of making additional API calls
        const todayDate = new Date(today)
        const fiveDaysFromNow = new Date(todayDate)
        fiveDaysFromNow.setDate(todayDate.getDate() + 5)

        // Extract upcoming deadlines from already fetched issues
        const upcomingDeadlines = assignedIssuesOpen.issues
            .filter(issue => {
                if (!issue.due_date) return false
                const dueDate = new Date(issue.due_date)
                return dueDate >= todayDate && dueDate <= fiveDaysFromNow
            })
            .slice(0, 5)

        // Count overdue tasks from already fetched issues
        const overdueTasks = assignedIssuesOpen.issues.filter(issue => {
            if (!issue.due_date) return false
            return new Date(issue.due_date) < todayDate
        }).length

        // Calculate on-track projects (status = 1 means active/open in Redmine)
        const onTrackProjects = projects.projects?.filter(project => project.status === 1).length || 0

        // Calculate completed this week (issues closed/resolved this week)
        const completedThisWeek = assignedIssuesClosed.total_count || 0

        return {
            assignedIssues: assignedIssuesOpen.total_count || 0,
            recentActivity: activity.activities || [],
            upcomingDeadlines: upcomingDeadlines,
            timeSpentToday: timeEntries.time_entries?.reduce((total, entry) => total + entry.hours, 0) || 0,
            projectsCount: projects.total_count || projects.projects?.length || 0,
            projects: projects.projects?.slice(0, 10) || [],
            onTrackProjects: onTrackProjects,
            overdueTasks: overdueTasks,
            completedThisWeek: completedThisWeek
        }
    }

    async getActivities(params?: any): Promise<{ activities: ActivityItem[] }> {
        try {
            // getActivities called

            // Fetch more issues to ensure we get enough activities
            const limit = params?.limit || 40
            const issueParams: any = {
                sort: 'updated_on:desc',
                limit: Math.max(limit, 50), // Fetch at least 50 issues
                include: 'journals', // Include journals in the initial request to avoid N+1 queries
                status_id: '*' // Include all statuses (open and closed)
            }

            // If project_id is provided, filter by project
            if (params?.project_id) {
                issueParams.project_id = params.project_id
            }

            // Fetching issues with params

            // Fetch recent issues with journals in a single request
            const recentIssues = await this.getIssues(issueParams)

            // Fetched issues count

            // Process journals from all issues
            const activities: ActivityItem[] = []

            for (const issue of recentIssues.issues || []) {
                // Add issue creation as an activity if it's recent
                if (issue.created_on) {
                    activities.push({
                        id: issue.id * 1000000, // Use large multiplier to avoid ID conflicts with journals
                        type: 'issue_created',
                        title: `Issue #${issue.id}: ${issue.subject}`,
                        description: `Created issue: ${issue.subject}`,
                        user: issue.author,
                        created_on: issue.created_on,
                        project: issue.project,
                        issue: issue,
                        notes: '',
                        details: []
                    })
                }

                // Add journals as activities
                if (issue.journals && issue.journals.length > 0) {
                    for (const journal of issue.journals) {
                        activities.push({
                            id: journal.id,
                            type: journal.notes ? 'comment_added' : 'issue_updated',
                            title: `Issue #${issue.id}: ${issue.subject}`,
                            description: journal.notes || '',
                            user: journal.user,
                            created_on: journal.created_on,
                            project: issue.project,
                            issue: issue,
                            notes: journal.notes,
                            details: journal.details
                        })
                    }
                }
            }

            // Total activities before sort

            // Sort by created_on (most recent first) and limit results
            const sortedActivities = activities
                .sort((a, b) => new Date(b.created_on).getTime() - new Date(a.created_on).getTime())
                .slice(0, limit)

            // Returning activities count

            return { activities: sortedActivities }
        } catch (error) {
            console.error('[RedmineClient] Error fetching activities:', error)
            return { activities: [] }
        }
    }

    // Time Tracking
    async getTimeEntries(params?: any): Promise<{ time_entries: TimeEntry[], total_count: number }> {
        const response = await this.client.get('/time_entries.json', { params })
        return response.data
    }

    async getTimeEntry(id: number): Promise<TimeEntry> {
        const response = await this.client.get(`/time_entries/${id}.json`)
        return response.data.time_entry
    }

    async createTimeEntry(timeEntry: Partial<TimeEntry>): Promise<TimeEntry> {
        const response = await this.client.post('/time_entries.json', { time_entry: timeEntry })
        return response.data.time_entry
    }

    async updateTimeEntry(id: number, timeEntry: Partial<TimeEntry>): Promise<TimeEntry> {
        const response = await this.client.put(`/time_entries/${id}.json`, { time_entry: timeEntry })
        return response.data.time_entry
    }

    async deleteTimeEntry(id: number): Promise<void> {
        await this.client.delete(`/time_entries/${id}.json`)
    }

    async getTimeEntryActivities(): Promise<{ time_entry_activities: TimeEntryActivity[] }> {
        const response = await this.client.get('/enumerations/time_entry_activities.json')
        return response.data
    }

    // Documents
    async getDocuments(projectId?: number): Promise<{ documents: Document[] }> {
        const url = projectId ? `/projects/${projectId}/documents.json` : '/documents.json'
        const response = await this.client.get(url)
        return response.data
    }

    async getDocument(id: number): Promise<Document> {
        const response = await this.client.get(`/documents/${id}.json`)
        return response.data.document
    }

    async createDocument(projectId: number, document: Partial<Document>): Promise<Document> {
        const response = await this.client.post(`/projects/${projectId}/documents.json`, { document })
        return response.data.document
    }

    async updateDocument(id: number, document: Partial<Document>): Promise<Document> {
        const response = await this.client.put(`/documents/${id}.json`, { document })
        return response.data.document
    }

    async deleteDocument(id: number): Promise<void> {
        await this.client.delete(`/documents/${id}.json`)
    }

    async getDocumentCategories(): Promise<{ document_categories: DocumentCategory[] }> {
        const response = await this.client.get('/enumerations/document_categories.json')
        return response.data
    }

    // News
    async getNews(projectId?: number): Promise<{ news: News[] }> {
        const url = projectId ? `/projects/${projectId}/news.json` : '/news.json'
        const response = await this.client.get(url)
        return response.data
    }

    async getNewsItem(id: number): Promise<News> {
        const response = await this.client.get(`/news/${id}.json`)
        return response.data.news
    }

    async createNews(projectId: number, news: Partial<News>): Promise<News> {
        const response = await this.client.post(`/projects/${projectId}/news.json`, { news })
        return response.data.news
    }

    async updateNews(id: number, news: Partial<News>): Promise<News> {
        const response = await this.client.put(`/news/${id}.json`, { news })
        return response.data.news
    }

    async deleteNews(id: number): Promise<void> {
        await this.client.delete(`/news/${id}.json`)
    }

    // Forums
    async getForums(projectId?: number): Promise<{ forums: Forum[] }> {
        const url = projectId ? `/projects/${projectId}/forums.json` : '/forums.json'
        const response = await this.client.get(url)
        return response.data
    }

    async getForum(id: number): Promise<Forum> {
        const response = await this.client.get(`/forums/${id}.json`)
        return response.data.forum
    }

    async getForumTopics(forumId: number): Promise<{ topics: ForumTopic[] }> {
        const response = await this.client.get(`/forums/${forumId}/topics.json`)
        return response.data
    }

    async getForumTopic(id: number): Promise<ForumTopic> {
        const response = await this.client.get(`/topics/${id}.json`)
        return response.data.topic
    }

    async createForumTopic(forumId: number, topic: Partial<ForumTopic>): Promise<ForumTopic> {
        const response = await this.client.post(`/forums/${forumId}/topics.json`, { topic })
        return response.data.topic
    }

    async getForumMessages(topicId: number): Promise<{ messages: ForumMessage[] }> {
        const response = await this.client.get(`/topics/${topicId}/messages.json`)
        return response.data
    }

    async createForumMessage(topicId: number, message: Partial<ForumMessage>): Promise<ForumMessage> {
        const response = await this.client.post(`/topics/${topicId}/messages.json`, { message })
        return response.data.message
    }

    // Wiki
    async getWikiPages(projectId: number): Promise<{ wiki_pages: WikiPage[] }> {
        const response = await this.client.get(`/projects/${projectId}/wiki/index.json`)
        return response.data
    }

    async getWikiPage(projectId: number, title: string): Promise<WikiPage> {
        const response = await this.client.get(`/projects/${projectId}/wiki/${title}.json`)
        return response.data.wiki_page
    }

    async createWikiPage(projectId: number, title: string, wikiPage: Partial<WikiPage>): Promise<WikiPage> {
        const response = await this.client.put(`/projects/${projectId}/wiki/${title}.json`, { wiki_page: wikiPage })
        return response.data.wiki_page
    }

    async updateWikiPage(projectId: number, title: string, wikiPage: Partial<WikiPage>): Promise<WikiPage> {
        const response = await this.client.put(`/projects/${projectId}/wiki/${title}.json`, { wiki_page: wikiPage })
        return response.data.wiki_page
    }

    async deleteWikiPage(projectId: number, title: string): Promise<void> {
        await this.client.delete(`/projects/${projectId}/wiki/${title}.json`)
    }

    // Versions/Releases
    async getVersions(projectId?: number): Promise<{ versions: Version[] }> {
        const url = projectId ? `/projects/${projectId}/versions.json` : '/versions.json'
        const response = await this.client.get(url)
        return response.data
    }

    async getVersion(id: number): Promise<Version> {
        const response = await this.client.get(`/versions/${id}.json`)
        return response.data.version
    }

    async createVersion(projectId: number, version: Partial<Version>): Promise<Version> {
        const response = await this.client.post(`/projects/${projectId}/versions.json`, { version })
        return response.data.version
    }

    async updateVersion(id: number, version: Partial<Version>): Promise<Version> {
        const response = await this.client.put(`/versions/${id}.json`, { version })
        return response.data.version
    }

    async deleteVersion(id: number): Promise<void> {
        await this.client.delete(`/versions/${id}.json`)
    }

    // Repositories
    async getRepositories(projectId: number): Promise<{ repositories: Repository[] }> {
        const response = await this.client.get(`/projects/${projectId}/repositories.json`)
        return response.data
    }

    async getRepository(projectId: number, id: number): Promise<Repository> {
        const response = await this.client.get(`/projects/${projectId}/repositories/${id}.json`)
        return response.data.repository
    }

    async getChangesets(projectId: number, repositoryId?: number): Promise<{ changesets: Changeset[] }> {
        const url = repositoryId
            ? `/projects/${projectId}/repositories/${repositoryId}/revisions.json`
            : `/projects/${projectId}/changesets.json`
        const response = await this.client.get(url)
        return response.data
    }

    async getChangeset(projectId: number, revision: string): Promise<Changeset> {
        const response = await this.client.get(`/projects/${projectId}/changesets/${revision}.json`)
        return response.data.changeset
    }

    // Administration
    async getRoles(): Promise<{ roles: Role[] }> {
        const response = await this.client.get('/roles.json')
        return response.data
    }

    async getRole(id: number): Promise<Role> {
        const response = await this.client.get(`/roles/${id}.json`)
        return response.data.role
    }

    async getGroups(): Promise<{ groups: Group[] }> {
        const response = await this.client.get('/groups.json')
        return response.data
    }

    async getGroup(id: number): Promise<Group> {
        const response = await this.client.get(`/groups/${id}.json?include=users`)
        return response.data.group
    }

    async createGroup(group: Partial<Group>): Promise<Group> {
        const response = await this.client.post('/groups.json', { group })
        return response.data.group
    }

    async updateGroup(id: number, group: Partial<Group>): Promise<Group> {
        const response = await this.client.put(`/groups/${id}.json`, { group })
        return response.data.group
    }

    async deleteGroup(id: number): Promise<void> {
        await this.client.delete(`/groups/${id}.json`)
    }

    async addUserToGroup(groupId: number, userId: number): Promise<void> {
        await this.client.post(`/groups/${groupId}/users.json`, { user_id: userId })
    }

    async removeUserFromGroup(groupId: number, userId: number): Promise<void> {
        await this.client.delete(`/groups/${groupId}/users/${userId}.json`)
    }

    // Custom Fields
    async getCustomFields(): Promise<{ custom_fields: CustomField[] }> {
        const response = await this.client.get('/custom_fields.json')
        return response.data
    }

    async getCustomField(id: number): Promise<CustomField> {
        const response = await this.client.get(`/custom_fields/${id}.json`)
        return response.data.custom_field
    }

    // Issue Statuses
    async getIssueStatuses(): Promise<{ issue_statuses: IssueStatus[] }> {
        const response = await this.client.get('/issue_statuses.json')
        return response.data
    }

    // Trackers
    async getTrackers(): Promise<{ trackers: Tracker[] }> {
        const response = await this.client.get('/trackers.json')
        return response.data
    }

    // Enumerations (Priorities, Activities, etc.)
    async getIssuePriorities(): Promise<{ issue_priorities: Priority[] }> {
        const response = await this.client.get('/enumerations/issue_priorities.json')
        return response.data
    }

    async getEnumerations(type?: string): Promise<{ enumerations: Enumeration[] }> {
        const url = type ? `/enumerations/${type}.json` : '/enumerations.json'
        const response = await this.client.get(url)
        return response.data
    }

    // Workflows
    async getWorkflows(params?: any): Promise<{ workflows: Workflow[] }> {
        const response = await this.client.get('/workflows.json', { params })
        return response.data
    }

    // Search
    async search(query: string, options?: {
        projectId?: number
        scope?: string[]
        attachments?: boolean
        openIssues?: boolean
        limit?: number
        offset?: number
    }): Promise<SearchResult> {
        const params = {
            q: query,
            limit: options?.limit || 10,
            offset: options?.offset || 0,
            ...(options?.projectId && { project_id: options.projectId }),
            ...(options?.scope && { scope: options.scope.join(',') }),
            ...(options?.attachments && { attachments: '1' }),
            ...(options?.openIssues && { open_issues: '1' })
        }

        const response = await this.client.get('/search.json', { params })
        return response.data
    }

    // Project Memberships
    async getProjectMemberships(projectId: number): Promise<{ memberships: any[] }> {
        // Fetch all memberships (Redmine default limit is 25, so we need to set a higher limit)
        const response = await this.client.get(`/projects/${projectId}/memberships.json`, {
            params: { limit: 100 }
        })
        return response.data
    }

    async addProjectMember(projectId: number, membership: { user_id?: number, group_id?: number, role_ids: number[] }): Promise<any> {
        const response = await this.client.post(`/projects/${projectId}/memberships.json`, { membership })
        return response.data.membership
    }

    async updateProjectMember(membershipId: number, membership: { role_ids: number[] }): Promise<any> {
        const response = await this.client.put(`/memberships/${membershipId}.json`, { membership })
        return response.data.membership
    }

    async removeProjectMember(membershipId: number): Promise<void> {
        await this.client.delete(`/memberships/${membershipId}.json`)
    }

    // File attachments
    async uploadFile(file: File): Promise<{ token: string }> {
        const formData = new FormData()
        formData.append('file', file)

        const response = await this.client.post('/uploads.json', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        return response.data.upload
    }

    // Issue watchers
    async getIssueWatchers(issueId: number): Promise<{ watchers: User[] }> {
        const response = await this.client.get(`/issues/${issueId}/watchers.json`)
        return response.data
    }

    async addIssueWatcher(issueId: number, userId: number): Promise<void> {
        await this.client.post(`/issues/${issueId}/watchers.json`, { user_id: userId })
    }

    async removeIssueWatcher(issueId: number, userId: number): Promise<void> {
        await this.client.delete(`/issues/${issueId}/watchers/${userId}.json`)
    }

    // Issue relations
    async getIssueRelations(issueId: number): Promise<{ relations: any[] }> {
        const response = await this.client.get(`/issues/${issueId}/relations.json`)
        return response.data
    }

    async createIssueRelation(issueId: number, relation: { issue_to_id: number, relation_type: string, delay?: number }): Promise<any> {
        const response = await this.client.post(`/issues/${issueId}/relations.json`, { relation })
        return response.data.relation
    }

    async deleteIssueRelation(relationId: number): Promise<void> {
        await this.client.delete(`/relations/${relationId}.json`)
    }

    // Utilities
    async testConnection(): Promise<boolean> {
        try {
            await this.getCurrentUser()
            return true
        } catch (error) {
            return false
        }
    }
}